import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import CreditService from '../services/CreditService';
import { useCreditsRefresh } from '../utils/creditsRefresh';
import styles from './CreditDisplay.module.css';

// Credits now come from two pools with different rules: the monthly allowance
// resets on the plan's renewal date (no rollover) while purchased top-ups sit
// in dated lots. The headline stays the single spendable total -- that is the
// number people check -- and the split is explanatory detail beneath it.
export function formatCreditDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export function daysUntil(value, now = Date.now()) {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return Math.ceil((parsed.getTime() - now) / 86400000);
}

const CreditDisplay = ({
  requiredCredits,
  compact = false,
  previewStatus = null,
  onResubscribe = null
}) => {
  const auth = useAuth();
  const { currentUser } = auth;
  // The auth context is a new object on every provider render (the first API
  // call storing the token is enough), so depending on it refetched credits
  // for nothing. Read it through a ref and refetch only when the user changes.
  const authRef = useRef(auth);
  authRef.current = auth;
  const [credits, setCredits] = useState(null);
  const [subscriptionActive, setSubscriptionActive] = useState(true);
  const [breakdown, setBreakdown] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCredits = useCallback(async () => {
    // previewStatus is the dev harness (/credit-display-test) handing us a
    // fixed payload: no auth, no network, so every state can be inspected at
    // any viewport. Production always passes null and fetches as before.
    if (previewStatus) {
      setCredits(previewStatus.balance);
      setSubscriptionActive(previewStatus.subscriptionActive);
      setBreakdown(
        previewStatus.allowance === null && previewStatus.topups === null
          ? null
          : {
              allowance: previewStatus.allowance || 0,
              topups: previewStatus.topups || 0,
              allowancePeriodEnd: previewStatus.allowancePeriodEnd,
              nextExpiry: previewStatus.nextExpiry
            }
      );
      setError(null);
      setLoading(false);
      return;
    }

    if (!currentUser) {
      return;
    }

    try {
      setLoading(true);
      CreditService.setAuthContext(authRef.current);
      const status = await CreditService.getCreditStatus();
      setCredits(status.balance);
      setSubscriptionActive(status.subscriptionActive);
      // Null when the backend predates the buckets; the breakdown is then
      // simply not rendered rather than shown as "0 allowance".
      setBreakdown(
        status.allowance === null && status.topups === null
          ? null
          : {
              allowance: status.allowance || 0,
              topups: status.topups || 0,
              allowancePeriodEnd: status.allowancePeriodEnd,
              nextExpiry: status.nextExpiry
            }
      );
      setError(null);
    } catch (err) {
      console.error('Error getting credits:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentUser, previewStatus]);

  useEffect(() => {
    fetchCredits();
  }, [fetchCredits]);

  // Refreshing is pointless against a fixed preview payload.
  useCreditsRefresh(fetchCredits, !previewStatus);

  if (loading && credits === null) {
    return (
      <div className={styles.creditDisplay}>
        <div className={styles.loading}>
          Loading credits...
        </div>
      </div>
    );
  }

  if (error) {
    console.error('CreditDisplay error:', error);
    return (
      <div className={styles.creditDisplay}>
        <div className={styles.error}>
          Error loading credits.
          <button
            onClick={fetchCredits}
            className={styles.retryButton}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if ((!currentUser && !previewStatus) || credits === null) {
    return null;
  }

  // Lapsed subscriber with a balance: the number is still theirs, it just
  // cannot be spent until they resubscribe. Show it greyed with the reason
  // rather than hiding it (hiding reads as "my credits were taken").
  const paused = !subscriptionActive && credits > 0;
  const resetDate = breakdown ? formatCreditDate(breakdown.allowancePeriodEnd) : null;
  // In compact mode (the dashboard header) the balance and a status chip are
  // the whole story. The allowance/top-up split and the paused explanation are
  // reference detail: they belong in Settings, next to the Resubscribe button,
  // not stacked into a header that goes single-column on a phone.
  const showBreakdown =
    !compact && Boolean(breakdown) && (breakdown.allowance > 0 || breakdown.topups > 0);

  return (
    <div
      className={`${styles.creditDisplay} ${paused ? styles.paused : ''} ${compact ? styles.compactRoot : ''}`}
    >
      <div className={styles.balance}>
        <span className={styles.label}>Credits:</span>
        <span className={`${styles.amount} ${paused ? styles.amountPaused : ''}`}>
          {credits.toFixed(1)}
        </span>
        {paused && (
          compact ? (
            // The chip carries the whole message here, so make it the action.
            <Link
              to="/settings"
              className={`${styles.pausedChip} ${styles.pausedChipLink}`}
              title="Your credits are paused. Resubscribe to convert new books."
            >
              Paused
            </Link>
          ) : (
            <span className={styles.pausedChip}>Paused</span>
          )
        )}
      </div>
      {showBreakdown && (
        <div className={styles.breakdown}>
          <span className={styles.breakdownItem}>
            {breakdown.allowance.toFixed(1)} monthly
            {resetDate && <span className={styles.breakdownNote}> · resets {resetDate}</span>}
          </span>
          <span className={styles.breakdownItem}>
            {breakdown.topups.toFixed(1)} top-up
          </span>
        </div>
      )}
      {paused && !compact && (
        <div className={styles.pausedNote}>
          Your credits are paused. Resubscribe to convert new books.
          {onResubscribe ? (
            // Already on Settings, where /settings is a dead click. The caller
            // points us at the plan buttons instead.
            <button type="button" onClick={onResubscribe} className={styles.pausedLink}>
              Resubscribe
            </button>
          ) : (
            <Link to="/settings" className={styles.pausedLink}>Resubscribe</Link>
          )}
        </div>
      )}
      {requiredCredits > 0 && (
        <div className={styles.required}>
          <span className={styles.label}>Required:</span>
          <span className={`${styles.amount} ${credits < requiredCredits ? styles.insufficient : ''}`}>
            {requiredCredits.toFixed(1)}
          </span>
        </div>
      )}
      {requiredCredits > 0 && credits < requiredCredits && (
        <div className={styles.warning}>
          Insufficient credits. Please purchase more to continue.
        </div>
      )}
    </div>
  );
};

export default CreditDisplay;
